import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Eye, EyeOff, Lock, Building } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";


const Onboarding = () => {

	const navigate = useNavigate();
	const { user, userProfile, refreshUserData } = useAuth();

	const [isVerifying, setIsVerifying] = useState(false);
	const [verificationChecked, setVerificationChecked] = useState(false);

	const urlParams = new URLSearchParams(window.location.search);
	const token = urlParams.get('token');
	const type = urlParams.get('type') as 'signup' | 'invite' | 'magiclink' | 'recovery' | 'email_change' | 'email';

	// --- Handle session from invite URL ---
	useEffect(() => {

		const handleVerification = async () => {

			if (token && type) {
				setIsVerifying(true);
				/* if (user) {
					// If already signed in, sign out first
					await supabase.auth.signOut();
				} */
				const { error } = await supabase.auth.verifyOtp({
					token_hash: token as string,
					type
				});
				if (error) {
					console.error("Error verifying token:", error);
					toast.error(error.message);
				}

				setIsVerifying(false);
				setVerificationChecked(true);

			} else {
				setVerificationChecked(true);
			}
		};

		handleVerification();
	}, [token, type, user]);

	const [isLoading, setIsLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [password, setPassword] = useState("");
	const [organizationName, setOrganizationName] = useState<string>("");
	const [formData, setFormData] = useState({
		firstName: userProfile?.first_name || "",
		lastName: userProfile?.last_name || ""
	});
	const { t } = useTranslation();

	// Password validation criteria
	const hasLowercase = /[a-z]/.test(password);
	const hasUppercase = /[A-Z]/.test(password);
	const hasDigit = /[0-9]/.test(password);
	const hasMinLength = password.length >= 6;
	const isPasswordValid = hasLowercase && hasUppercase && hasDigit && hasMinLength;

	// Fetch organization name
	useEffect(() => {
		if (user) {
			fetchOrganizationName();
		}
	}, [user]);

	// Update form data when userProfile changes
	useEffect(() => {
		if (userProfile) {
			setFormData({
				firstName: userProfile.first_name || "",
				lastName: userProfile.last_name || ""
			});
		}
	}, [userProfile]);

	const fetchOrganizationName = async () => {
		try {
			// Get user's organization UUID first
			const { data: userOrgData, error: userOrgError } = await supabase
				.from('user_organization')
				.select('organization_uuid')
				.eq('user_uuid', user?.id)
				.single();

			if (userOrgError) {
				console.error("Error fetching user organization:", userOrgError);
				return;
			}

			if (userOrgData?.organization_uuid) {
				// Now get the organization name
				const { data: orgData, error: orgError } = await supabase
					.from('organizations')
					.select('name')
					.eq('id', userOrgData.organization_uuid)
					.single();

				if (orgError) {
					console.error("Error fetching organization:", orgError);
					return;
				}

				if (orgData) {
					setOrganizationName(orgData.name);
				}
			}
		} catch (error) {
			console.error("Unexpected error fetching organization:", error);
		}
	};

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPassword(e.target.value);
	};

	const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({ ...prev, [name]: value }));
	};

	const togglePasswordVisibility = () => {
		setShowPassword(prev => !prev);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!user) {
			toast.error(t("onboarding.user.noUser"));
			return;
		}

		if (!formData.firstName.trim()) {
			toast.error(t("onboarding.user.firstNameRequired"));
			return;
		}

		if (!formData.lastName.trim()) {
			toast.error(t("onboarding.user.lastNameRequired"));
			return;
		}

		if (!isPasswordValid) {
			toast.error(t("onboarding.user.passwordIsNotValid"));
			return;
		}

		setIsLoading(true);

		try {
			// Step 1: Update password
			const { error: passwordError } = await supabase.auth.updateUser({
				password
			});

			if (passwordError) {
				throw new Error(`Password update failed: ${passwordError.message}`);
			}

			// Step 2: Update profile information
			const { error: profileError } = await supabase
				.from('profiles')
				.update({
					first_name: formData.firstName.trim(),
					last_name: formData.lastName.trim()
				})
				.eq('id', user.id);

			if (profileError) {
				throw new Error(`Profile update failed: ${profileError.message}`);
			}

			// Step 3: Mark onboarding as completed
			const { error: onboardingError } = await supabase
				.from('onboardings')
				.update({
					completed: true
				})
				.eq('id', user.id);

			if (onboardingError) {
				throw new Error(`Onboarding completion failed: ${onboardingError.message}`);
			}

			// Refresh user data to update the UI
			await refreshUserData();

			toast.success(t("onboarding.user.onboardingCompleted"));

			// Redirect to home page
			navigate("/");

		} catch (error) {
			console.error("Error completing onboarding:", error);
			toast.error(error instanceof Error ? error.message : "There was an error completing your onboarding");
		} finally {
			setIsLoading(false);
		}
	};

	// If verifying, show nothing or a loader
	if (isVerifying) {
		return <div className="flex min-h-screen items-center justify-center">Verifying...</div>;
	}

	// If verification is checked, and there is no user and no token, redirect to auth
	if (verificationChecked && !user && !token) {
		return <Navigate to="/auth" replace />;
	}

	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4">
			<div className="absolute top-4 right-4">
				<LanguageSelector />
			</div>
			<div className="w-full max-w-2xl">
				<Card className="shadow-lg">
					<CardHeader className="text-center pb-6">
						<div className="mx-auto mb-4 flex items-center justify-center">
							<div className="bg-digger-blue-light rounded-md w-16 h-16 flex items-center justify-center mr-3">
								<img
									src="/lovable-uploads/1f16b574-2585-4f89-a895-9af7f8c1fa72.png"
									alt="digger logo icon"
									className="w-10 h-10"
								/>
							</div>
							<span className="text-digger-blue text-3xl font-bold">digger</span>
						</div>
						<CardTitle className="text-2xl">{t("onboarding.welcome")}</CardTitle>
						<CardDescription className="mt-2">

						</CardDescription>
						{organizationName && (
							<div className="mt-4 flex items-center justify-center">
								<Building className="h-5 w-5 text-gray-500 mr-2" />
								<p className="text-lg font-medium">{organizationName}</p>
							</div>
						)}
					</CardHeader>

					<form onSubmit={handleSubmit}>
						<CardContent className="space-y-6">
							<div className="space-y-1">
								<h3 className="text-lg font-medium">{t("onboarding.user.profile")}</h3>
							</div>

							<div className="grid grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label htmlFor="firstName">{t("onboarding.user.firstName")}</Label>
									<Input
										id="firstName"
										name="firstName"
										value={formData.firstName}
										onChange={handleProfileChange}
										placeholder={t("onboarding.user.enterFirstName")}
										required
									/>
								</div>

								<div className="space-y-2">
									<Label htmlFor="lastName">{t("onboarding.user.lastName")}</Label>
									<Input
										id="lastName"
										name="lastName"
										value={formData.lastName}
										onChange={handleProfileChange}
										placeholder={t("onboarding.user.enterLastName")}
										required
									/>
								</div>
							</div>

							<div className="border-t pt-6">
								<div className="space-y-1">
									<h3 className="text-lg font-medium">{t("onboarding.user.security")}</h3>
								</div>

								<div className="space-y-2 mt-4">
									<Label htmlFor="password">{t("onboarding.user.password.new")}</Label>
									<div className="relative">
										<Input
											id="password"
											type={showPassword ? "text" : "password"}
											value={password}
											onChange={handlePasswordChange}
											placeholder={t("onboarding.user.password.enterNew")}
											required
										/>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											className="absolute right-2 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
											onClick={togglePasswordVisibility}
										>
											{showPassword ?
												<EyeOff className="h-4 w-4" /> :
												<Eye className="h-4 w-4" />
											}
										</Button>
									</div>

									<div className="text-sm mt-2">
										<p className="mb-1">{t("onboarding.user.password.requirements")}</p>
										<ul className="space-y-1 list-disc list-inside text-muted-foreground">
											<li className={hasLowercase ? "text-green-500" : ""}>
												{t("onboarding.user.password.lowercase")}
											</li>
											<li className={hasUppercase ? "text-green-500" : ""}>
												{t("onboarding.user.password.uppercase")}
											</li>
											<li className={hasDigit ? "text-green-500" : ""}>
												{t("onboarding.user.password.number")}
											</li>
											<li className={hasMinLength ? "text-green-500" : ""}>
												{t("onboarding.user.password.minLength")}
											</li>
										</ul>
									</div>
								</div>
							</div>
						</CardContent>

						<CardFooter>
							<Button
								type="submit"
								className="w-full"
								disabled={isLoading || !isPasswordValid}
							>
								{isLoading ?
									t("onboarding.user.completing") :
									t("onboarding.user.complete")
								}
								<Save className="ml-2 h-4 w-4" />
							</Button>
						</CardFooter>
					</form>
				</Card>
			</div>
		</div>
	);
};

export default Onboarding;